import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import client from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { roleLabel, permissionLabels } from "@/lib/i18n";
import { hasGlobalUserManagement } from "@/lib/permissions";
import { useRoleCatalogue } from "@/hooks/useRoleCatalogue";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import {
  Plus,
  Search,
  Users as UsersIcon,
  Pencil,
  KeyRound,
  Trash2,
  Ban,
  RotateCcw,
  Loader2,
  Copy,
  Check,
  Mail,
  AlertTriangle,
  Info,
  RefreshCw,
} from "lucide-react";

// Role codes come from GET /api/roles (useRoleCatalogue) — never a constant
// duplicated here. "User management" is no longer a separate permission
// toggle either: it is the MHQ_USER_MANAGER / DEALER_USER_MANAGER role, so
// it appears as just another chip/column like every other capability.

// Shared pill styling for every role/permission toggle in this file — a
// permission (e.g. "User Management") is presented identically to a role,
// since from the user's point of view it's just another thing to grant.
const pillClass = (active) =>
  `rounded-full border px-3 py-1 text-xs transition ${
    active ? "border-primary bg-primary text-primary-foreground" : "border-border bg-background hover:bg-muted"
  } disabled:opacity-50`;

/* ============================================================
   Dealers-mapped info popover — shown on the users table's Dealers column
   ============================================================ */
function DealersInfoPopover({ names, t }) {
  if (!names || names.length === 0) return null;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex h-5 w-5 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground"
          data-testid="dealers-info-trigger"
          aria-label={t("users.columns.dealers")}
        >
          <Info className="h-3.5 w-3.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-2 text-xs" align="start">
        <ul className="space-y-1">
          {names.map((n, i) => (
            <li key={i}>{n || "—"}</li>
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  );
}

/* ============================================================
   Dealer access grid — one row per mapped dealer, one checkbox column
   per dealer role plus the "User Management" permission
   ============================================================ */
function DealerAccessGrid({ value, onChange, disabled, assignableDealers, lang, t }) {
  const { dealerRoles, permissionsOf } = useRoleCatalogue();
  const [pickerOpen, setPickerOpen] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    function handler(e) {
      if (boxRef.current && !boxRef.current.contains(e.target)) setPickerOpen(false);
    }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const usedIds = new Set(value.map((g) => g.dealer_id));
  const options = (assignableDealers || []).filter((d) => !usedIds.has(d.id));

  const updateGrant = (dealerId, updater) => {
    onChange(value.map((g) => (g.dealer_id === dealerId ? updater(g) : g)));
  };
  const toggleRole = (dealerId, role) => {
    updateGrant(dealerId, (g) => ({
      ...g,
      roles: g.roles.includes(role) ? g.roles.filter((r) => r !== role) : [...g.roles, role],
    }));
  };
  const removeGrant = (dealerId) => onChange(value.filter((g) => g.dealer_id !== dealerId));
  const addGrant = (dealer) => {
    onChange([...value, { dealer_id: dealer.id, dealer_name: dealer.name, roles: [], permissions: [] }]);
    setPickerOpen(false);
  };

  return (
    <div>
      <Label className="data-label mb-2 block">{t("users.dealerAccess.title")}</Label>

      {value.length === 0 && (
        <p className="mb-2 text-xs text-muted-foreground" data-testid="dealer-access-empty">
          {t("users.dealerAccess.noneYet")}
        </p>
      )}

      {value.length > 0 && (
        <div className="mb-2 overflow-x-auto rounded-md border border-border">
          <table className="min-w-full text-xs" data-testid="dealer-access-grid">
            <thead className="thead">
              <tr>
                <th className="px-2 py-1.5 text-left">{t("dealers.columns.name")}</th>
                {dealerRoles.map((r) => (
                  <th key={r} className="px-2 py-1.5 text-center font-normal">
                    <span title={permissionLabels(permissionsOf(r), lang).join(", ")}>{roleLabel(r, lang)}</span>
                  </th>
                ))}
                <th className="px-2 py-1.5" />
              </tr>
            </thead>
            <tbody>
              {value.map((g) => (
                <tr key={g.dealer_id} className="border-t border-border" data-testid={`dealer-access-row-${g.dealer_id}`}>
                  <td className="px-2 py-1.5 font-medium">{g.dealer_name || g.dealer_id}</td>
                  {dealerRoles.map((r) => (
                    <td key={r} className="px-2 py-1.5 text-center">
                      <Checkbox
                        checked={g.roles.includes(r)}
                        onCheckedChange={() => toggleRole(g.dealer_id, r)}
                        disabled={disabled}
                        data-testid={`dealer-access-role-${g.dealer_id}-${r}`}
                        aria-label={roleLabel(r, lang)}
                      />
                    </td>
                  ))}
                  <td className="px-2 py-1.5 text-right">
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      disabled={disabled}
                      onClick={() => removeGrant(g.dealer_id)}
                      title={t("users.dealerAccess.removeDealer")}
                      data-testid={`dealer-access-remove-${g.dealer_id}`}
                    >
                      <Trash2 className="h-3.5 w-3.5 text-destructive" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="relative" ref={boxRef}>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={disabled || options.length === 0}
          onClick={() => setPickerOpen((o) => !o)}
          data-testid="add-dealer-btn"
          className="gap-1.5"
        >
          <Plus className="h-3.5 w-3.5" /> {t("users.dealerAccess.addDealer")}
        </Button>
        {options.length === 0 && (
          <p className="mt-1 text-xs text-muted-foreground">{t("users.dealerAccess.noAssignable")}</p>
        )}
        {pickerOpen && options.length > 0 && (
          <div className="absolute left-0 z-40 mt-1 max-h-64 w-72 overflow-y-auto rounded-md border border-border bg-popover shadow-lg">
            {options.map((d) => (
              <button
                key={d.id}
                type="button"
                data-testid={`dealer-access-pick-${d.id}`}
                onClick={() => addGrant(d)}
                className="flex w-full flex-col gap-0.5 border-b border-border px-3 py-2 text-left hover:bg-muted"
              >
                <span className="text-sm font-medium">{d.name}</span>
                <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                  {d.global_id}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ============================================================
   MHQ role chips + "User Management" permission
   ============================================================ */
function MhqRoleChips({ selected, onChange, disabled, lang }) {
  const { mhqRoles, permissionsOf } = useRoleCatalogue();
  const toggle = (v) => {
    if (disabled) return;
    onChange(selected.includes(v) ? selected.filter((r) => r !== v) : [...selected, v]);
  };
  return (
    <div>
      <Label className="data-label mb-2 block">Roles</Label>
      <div className="flex flex-wrap gap-1.5">
        {mhqRoles.map((r) => (
          <button
            type="button"
            key={r}
            data-testid={`role-toggle-${r}`}
            onClick={() => toggle(r)}
            disabled={disabled}
            // Hovering a role shows exactly what it grants, so an assigner
            // never has to guess from the name.
            title={permissionLabels(permissionsOf(r), lang).join(", ")}
            className={pillClass(selected.includes(r))}
          >
            {roleLabel(r, lang)}
          </button>
        ))}
      </div>
      {selected.length > 0 && (
        <p className="mt-2 text-xs text-muted-foreground" data-testid="mhq-roles-effective-permissions">
          {permissionLabels(
            [...new Set(selected.flatMap((r) => permissionsOf(r)))].sort(),
            lang,
          ).join(" · ")}
        </p>
      )}
    </div>
  );
}

/* ============================================================
   Scope toggle — only shown to global user-managers, who may create/edit
   either an MHQ or a Dealer user. A Dealer-scoped manager never sees this —
   they can only ever manage Dealer users (enforced centrally on the backend).
   ============================================================ */
function ScopeToggle({ scope, onChange, disabled, t }) {
  return (
    <div className="flex gap-1.5" data-testid="user-scope-toggle">
      {["mhq", "dealer"].map((s) => (
        <button
          type="button"
          key={s}
          data-testid={`user-scope-${s}`}
          onClick={() => onChange(s)}
          disabled={disabled}
          className={pillClass(scope === s)}
        >
          {t(`users.scope.${s}`)}
        </button>
      ))}
    </div>
  );
}

/* ============================================================
   Create-user dialog
   ============================================================ */
function CreateUserDialog({ open, onOpenChange, onCreated, me, assignableDealers, lang, t }) {
  const globalManager = hasGlobalUserManagement(me);
  const [scope, setScope] = useState(globalManager ? "mhq" : "dealer");
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [mhqRoles, setMhqRoles] = useState([]);
  const [dealerAccess, setDealerAccess] = useState([]);
  const [saving, setSaving] = useState(false);
  const [localOnly, setLocalOnly] = useState(false);
  const [result, setResult] = useState(null); // {email_sent, temp_password}
  const isSysAdmin = (me?.roles || []).includes("SYSTEM_ADMIN");

  useEffect(() => {
    if (open) {
      setScope(globalManager ? "mhq" : "dealer");
      setEmail("");
      setFirstName("");
      setLastName("");
      setMhqRoles([]);
      setDealerAccess([]);
      setLocalOnly(false);
      setResult(null);
    }
  }, [open, globalManager]);

  const validDealerAccess = dealerAccess.every((g) => g.roles.length || g.permissions.length);
  const canSubmit =
    email.trim() &&
    firstName.trim() &&
    (scope === "mhq" ? mhqRoles.length > 0 : dealerAccess.length > 0 && validDealerAccess);

  const submit = async () => {
    setSaving(true);
    try {
      const { data } = await client.post("/users", {
        email: email.trim(),
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        roles: scope === "mhq" ? mhqRoles : [],
        permissions: [],
        dealer_access:
          scope === "dealer"
            ? dealerAccess.map(({ dealer_id, roles, permissions }) => ({ dealer_id, roles, permissions }))
            : [],
        local_only: isSysAdmin && localOnly,
      });
      setResult({
        email_sent: data.email_sent,
        temp_password: data.temp_password,
        email: data.user.email,
        local_only: !!data.user.local_only,
        // A pending action after create = the Keycloak leg failed; the invitation
        // email is sent when it is retried (the password only exists once Keycloak has it).
        sync_failed: !data.user.local_only && !!data.user.pending_action,
      });
      onCreated();
      if (data.user.pending_action && !data.user.local_only) toast.warning("User created, but the Keycloak sync failed — retry it from the user list.");
      else if (data.email_sent) toast.success(`Invitation email sent to ${data.user.email}`);
      else if (data.temp_password) toast.warning("User created but email could not be sent — copy the temp password below.");
      else toast.success("User created");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="create-user-dialog">
        <DialogHeader>
          <DialogTitle>New user</DialogTitle>
          <DialogDescription>
            A temporary password is generated and emailed to the new user. They will be prompted to change it on first sign-in.
          </DialogDescription>
        </DialogHeader>

        {!result ? (
          <div className="space-y-4">
            <div>
              <Label className="data-label mb-1 block">Email</Label>
              <Input data-testid="new-user-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="data-label mb-1 block">First name</Label>
                <Input data-testid="new-user-first-name" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
              </div>
              <div>
                <Label className="data-label mb-1 block">Last name</Label>
                <Input data-testid="new-user-last-name" value={lastName} onChange={(e) => setLastName(e.target.value)} />
              </div>
            </div>
            {globalManager && <ScopeToggle scope={scope} onChange={setScope} disabled={saving} t={t} />}
            {isSysAdmin && (
              <label className="flex cursor-pointer items-center gap-2 text-sm" data-testid="new-user-local-only-label">
                <Checkbox
                  checked={localOnly}
                  onCheckedChange={(v) => setLocalOnly(v === true)}
                  disabled={saving}
                  data-testid="new-user-local-only"
                />
                <span>No SSO provisioning - Create local user only</span>
              </label>
            )}
            {scope === "mhq" ? (
              <MhqRoleChips selected={mhqRoles} onChange={setMhqRoles} disabled={saving} lang={lang} />
            ) : (
              <DealerAccessGrid
                value={dealerAccess}
                onChange={setDealerAccess}
                disabled={saving}
                assignableDealers={assignableDealers}
                lang={lang}
                t={t}
              />
            )}
          </div>
        ) : (
          <CreatedResultPanel result={result} />
        )}

        <DialogFooter>
          {!result ? (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
                Cancel
              </Button>
              <Button data-testid="new-user-save" onClick={submit} disabled={!canSubmit || saving}>
                {saving ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Plus className="mr-1.5 h-3.5 w-3.5" />}
                Create user
              </Button>
            </>
          ) : (
            <Button onClick={() => onOpenChange(false)} data-testid="create-user-done">
              Done
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ============================================================
   Post-create / reset result panel — shows temp password if email failed
   ============================================================ */
function CreatedResultPanel({ result }) {
  const [copied, setCopied] = useState(false);
  if (result.sync_failed) {
    return (
      <div className="flex gap-3 rounded-md border border-amber-500/40 bg-amber-500/5 p-4 text-sm" data-testid="result-sync-failed">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
        <div>
          <p className="font-medium">Created locally — Keycloak sync pending</p>
          <p className="mt-1 text-muted-foreground">
            <span className="font-mono">{result.email}</span> gets their invitation email as soon as you press Retry on the
            sync badge in the user list.
          </p>
        </div>
      </div>
    );
  }
  if (!result.email_sent && !result.temp_password) {
    return (
      <div className="flex gap-3 rounded-md border border-border p-4 text-sm" data-testid="result-no-password">
        <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
        <p className="text-muted-foreground">
          <span className="font-mono">{result.email}</span> was linked to an existing Keycloak account — no new
          temporary password was issued.
        </p>
      </div>
    );
  }
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(result.temp_password);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard blocked — ignore */
    }
  };

  if (result.email_sent) {
    return (
      <div className="flex gap-3 rounded-md border border-emerald-500/40 bg-emerald-500/5 p-4 text-sm" data-testid="result-email-sent">
        <Mail className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
        <div>
          <p className="font-medium">Invitation email sent</p>
          <p className="mt-1 text-muted-foreground">
            <span className="font-mono">{result.email}</span> will receive a temporary password.
          </p>
        </div>
      </div>
    );
  }
  return (
    <div className="space-y-3 rounded-md border border-amber-500/40 bg-amber-500/5 p-4 text-sm" data-testid="result-email-failed">
      <div className="flex gap-3">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
        <div>
          <p className="font-medium">Email delivery failed</p>
          <p className="mt-1 text-muted-foreground">
            Share this one-time password with <span className="font-mono">{result.email}</span> securely. It will not be shown again.
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2 rounded-md border border-border bg-background p-2">
        <code className="flex-1 font-mono text-sm" data-testid="result-temp-password">
          {result.temp_password}
        </code>
        <Button size="sm" variant="ghost" onClick={copy} data-testid="result-copy-btn">
          {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
        </Button>
      </div>
    </div>
  );
}

/* ============================================================
   Edit-user dialog
   ============================================================ */
function EditUserDialog({ open, onOpenChange, user, onSaved, me, assignableDealers, lang, t }) {
  const globalManager = hasGlobalUserManagement(me);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [scope, setScope] = useState("dealer");
  const [mhqRoles, setMhqRoles] = useState([]);
  const [dealerAccess, setDealerAccess] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && user) {
      const isDealerUser = (user.dealer_access || []).length > 0;
      setFirstName(user.first_name || "");
      setLastName(user.last_name || "");
      setScope(isDealerUser ? "dealer" : "mhq");
      setMhqRoles(!isDealerUser ? user.roles || [] : []);
      setDealerAccess(
        (user.dealer_access || []).map((g, i) => ({
          dealer_id: g.dealer_id,
          dealer_name: user.dealer_names?.[i] || g.dealer_id,
          roles: g.roles || [],
          permissions: g.permissions || [],
        })),
      );
    }
  }, [open, user]);

  const validDealerAccess = dealerAccess.every((g) => g.roles.length || g.permissions.length);
  const canSubmit =
    firstName.trim() && (scope === "mhq" ? mhqRoles.length > 0 : dealerAccess.length > 0 && validDealerAccess);

  const submit = async () => {
    setSaving(true);
    try {
      const body = { first_name: firstName.trim(), last_name: lastName.trim() };
      // Omit the fields the actor isn't touching entirely (not even as an
      // empty array) — the backend treats `roles`/`permissions` presence as
      // "the actor is trying to edit the MHQ side," which a Dealer-scoped
      // manager may never do, even to a no-op empty list. See
      // docs/developer/13-authorization.md.
      if (scope === "mhq") {
        body.roles = mhqRoles;
        body.permissions = [];
      } else {
        body.dealer_access = dealerAccess.map(({ dealer_id, roles, permissions }) => ({ dealer_id, roles, permissions }));
      }
      await client.patch(`/users/${user.id}`, body);
      toast.success("User updated");
      onSaved();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  if (!user) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="edit-user-dialog">
        <DialogHeader>
          <DialogTitle>Edit user</DialogTitle>
          <DialogDescription className="font-mono text-xs">{user.email}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="data-label mb-1 block">First name</Label>
              <Input data-testid="edit-user-first-name" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
            </div>
            <div>
              <Label className="data-label mb-1 block">Last name</Label>
              <Input data-testid="edit-user-last-name" value={lastName} onChange={(e) => setLastName(e.target.value)} />
            </div>
          </div>
          {globalManager && <ScopeToggle scope={scope} onChange={setScope} disabled={saving} t={t} />}
          {scope === "mhq" ? (
            <MhqRoleChips selected={mhqRoles} onChange={setMhqRoles} disabled={saving} lang={lang} />
          ) : (
            <DealerAccessGrid
              value={dealerAccess}
              onChange={setDealerAccess}
              disabled={saving}
              assignableDealers={assignableDealers}
              lang={lang}
              t={t}
            />
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button onClick={submit} disabled={!canSubmit || saving} data-testid="edit-user-save">
            {saving && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ============================================================
   Reset-password dialog
   ============================================================ */
function ResetPasswordDialog({ open, onOpenChange, user }) {
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (open) setResult(null);
  }, [open]);

  const submit = async () => {
    setSaving(true);
    try {
      const { data } = await client.post(`/users/${user.id}/reset-password`, {});
      setResult({ email_sent: data.email_sent, temp_password: data.temp_password, email: user.email });
      if (data.email_sent) toast.success(`Reset email sent to ${user.email}`);
      else toast.warning("Email could not be sent — copy the temp password below.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed");
    } finally {
      setSaving(false);
    }
  };

  if (!user) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="reset-password-dialog">
        <DialogHeader>
          <DialogTitle>Reset password</DialogTitle>
          <DialogDescription>
            A new temporary password will be generated and emailed to <span className="font-mono">{user.email}</span>. They will be required to change it on next sign-in.
          </DialogDescription>
        </DialogHeader>
        {result && <CreatedResultPanel result={result} />}
        <DialogFooter>
          {!result ? (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
              <Button onClick={submit} disabled={saving} data-testid="confirm-reset-btn">
                {saving ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <KeyRound className="mr-1.5 h-3.5 w-3.5" />}
                Reset & email
              </Button>
            </>
          ) : (
            <Button onClick={() => onOpenChange(false)}>Done</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ============================================================
   Sync-status badge — shows Keycloak provisioning state for a user row.
   Modeled on frontend/src/pages/dns/DnsSetup.jsx's error+retry pattern: a
   destructive badge + the failing system's error_message when something
   failed, a neutral "Syncing…" badge while in flight, nothing once every
   system reports "ok" (pending_action cleared).

   Keycloak is the only system left: as of 2026-09-24 the CMS builds its own
   users from Keycloak's roles and groups at login, so this app no longer
   pushes user changes to it and `sync_status` only ever carries a
   "keycloak" key (see the "DISABLED 2026-09-24" blocks in
   backend/user_provisioning.py). The loop below is deliberately still
   generic over sync_status, so restoring the CMS leg needs no change here
   beyond the label.
   ============================================================ */
function SyncStatusBadge({ user, onRetry, busy }) {
  if (user.local_only) {
    return (
      <Badge variant="outline" data-testid={`local-only-${user.id}`}>
        Local only
      </Badge>
    );
  }
  if (!user.pending_action) return null;

  const syncStatus = user.sync_status || {};
  const failed = Object.entries(syncStatus).find(([, s]) => s?.status === "error");

  if (!failed) {
    return (
      <Badge variant="secondary" className="gap-1" data-testid={`sync-pending-${user.id}`}>
        <Loader2 className="h-3 w-3 animate-spin" />
        Syncing…
      </Badge>
    );
  }

  const [system, status] = failed;
  return (
    <div className="flex items-center gap-2" data-testid={`sync-error-${user.id}`}>
      <Badge variant="destructive" className="gap-1">
        <AlertTriangle className="h-3 w-3" />
        {/* Was: {system === "keycloak" ? "Keycloak" : "CMS"} — restore the
            ternary if the CMS leg is ever re-enabled. */}
        Keycloak sync failed
      </Badge>
      {status?.error_message && (
        <span className="max-w-[24rem] truncate text-[10px] text-muted-foreground" title={status.error_message}>
          {status.error_message}
        </span>
      )}
      <Button
        size="sm"
        variant="outline"
        className="h-6 gap-1 px-2 text-[10px]"
        onClick={() => onRetry(user)}
        disabled={busy}
        data-testid={`retry-sync-btn-${user.id}`}
      >
        {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
        Retry
      </Button>
    </div>
  );
}

/* ============================================================
   Main Users page
   ============================================================ */
export default function Users() {
  const { user: me } = useAuth();
  const { t, lang } = useI18n();
  const [items, setItems] = useState(null);
  const [assignableDealers, setAssignableDealers] = useState([]);
  const [q, setQ] = useState("");
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [resetting, setResetting] = useState(null);
  const [deactivating, setDeactivating] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [busyId, setBusyId] = useState(null);
  // Bulk selection, same shape as DealersList.jsx: a Set of ids, scoped to
  // whatever the current filter is showing.
  const [selected, setSelected] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkReport, setBulkReport] = useState(null);

  const refresh = async () => {
    const { data } = await client.get("/users", { params: { q, include_deleted: includeDeleted } });
    setItems(data.items);
  };

  // A user can never bulk-action themselves — the backend rejects it per
  // target anyway, but excluding it here keeps the count honest and the
  // confirm text accurate.
  const selectableRows = (items || []).filter((u) => u.id !== me.id);
  const allSelectedOnPage = selectableRows.length > 0 && selectableRows.every((u) => selected.has(u.id));
  const someSelectedOnPage = selectableRows.some((u) => selected.has(u.id)) && !allSelectedOnPage;

  const toggleSelectAll = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allSelectedOnPage) selectableRows.forEach((u) => next.delete(u.id));
      else selectableRows.forEach((u) => next.add(u.id));
      return next;
    });
  };

  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  /**
   * Both bulk actions go through one runner because the response shape is
   * identical: per-user rows with ok / pending / error. `pending` is NOT
   * success — for a hard delete it means the document is still there because
   * Keycloak did not confirm — so it is reported separately and the row is
   * kept selected so a retry is one click away.
   */
  const runBulk = async (kind) => {
    const ids = [...selected];
    if (ids.length === 0) return;
    const verb = kind === "delete" ? "permanently delete" : "deactivate";
    const warning =
      kind === "delete"
        ? "\n\nThis cannot be undone."
        : "\n\nThey will be locked out immediately; you can restore them later.";
    if (!window.confirm(`Are you sure you want to ${verb} ${ids.length} user(s)?${warning}`)) return;

    setBulkBusy(true);
    try {
      const path = kind === "delete" ? "/users/hard-delete/bulk" : "/users/deactivate/bulk";
      const { data } = await client.post(path, { user_ids: ids });
      setBulkReport({ kind, ...data });
      // Keep only the rows that did NOT finish, so the obvious next action
      // (fix Keycloak, press the button again) operates on exactly those.
      const unfinished = (data.results || []).filter((r) => r.status !== "ok").map((r) => r.user_id);
      setSelected(new Set(unfinished));
      if (data.ok > 0) toast.success(`${data.ok} user(s) ${kind === "delete" ? "deleted" : "deactivated"}`);
      if (data.pending > 0) toast.error(`${data.pending} not confirmed by Keycloak — see the report`);
      if (data.error > 0) toast.error(`${data.error} rejected — see the report`);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk action failed");
    } finally {
      setBulkBusy(false);
    }
  };

  const refreshAssignableDealers = async () => {
    try {
      const { data } = await client.get("/users/assignable-dealers");
      setAssignableDealers(data.items || []);
    } catch {
      setAssignableDealers([]);
    }
  };

  useEffect(() => {
    setItems(null);
    refresh().catch(() => setItems([]));
  }, [includeDeleted]);

  useEffect(() => {
    refreshAssignableDealers();
  }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      refresh().catch(() => {});
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  const deactivate = async () => {
    if (!deactivating) return;
    setBusyId(deactivating.id);
    try {
      await client.delete(`/users/${deactivating.id}`);
      toast.success("User deactivated");
      setDeactivating(null);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Deactivate failed");
    } finally {
      setBusyId(null);
    }
  };

  const restore = async (u) => {
    setBusyId(u.id);
    try {
      await client.post(`/users/${u.id}/restore`, {});
      toast.success("User restored");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Restore failed");
    } finally {
      setBusyId(null);
    }
  };

  const hardDelete = async () => {
    if (!deleting) return;
    setBusyId(deleting.id);
    try {
      const { data } = await client.delete(`/users/${deleting.id}/hard`);
      if (data?.deleted) {
        toast.success("User permanently deleted");
      } else {
        toast.error("Delete is pending — Keycloak/CMS sync failed, see the sync badge to retry");
      }
      setDeleting(null);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally {
      setBusyId(null);
    }
  };

  const retrySync = async (u) => {
    setBusyId(u.id);
    try {
      await client.post(`/users/${u.id}/retry-sync`, {});
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Retry failed");
    } finally {
      setBusyId(null);
    }
  };

  const stats = useMemo(() => {
    if (!items) return null;
    const active = items.filter((u) => !u.deleted).length;
    return { total: items.length, active, deactivated: items.length - active };
  }, [items]);

  return (
    <div className="mx-auto max-w-6xl p-6 md:p-10" data-testid="users-page">
      <div className="mb-6 flex items-end justify-between gap-3">
        <div>
          <p className="data-label mb-2">USER MANAGEMENT</p>
          <h1 className="text-3xl font-bold tracking-tight md:text-4xl">Users</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Create, edit, reset passwords, and deactivate MHQ and dealer users.
          </p>
        </div>
        <Button data-testid="new-user-btn" onClick={() => setCreating(true)} className="gap-1.5">
          <Plus className="h-4 w-4" /> New user
        </Button>
      </div>

      <div className="mb-4 flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            data-testid="users-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by email or name…"
            className="pl-8"
          />
        </div>
        <label className="flex items-center gap-2 text-xs text-muted-foreground">
          <input
            type="checkbox"
            data-testid="show-deleted-toggle"
            checked={includeDeleted}
            onChange={(e) => setIncludeDeleted(e.target.checked)}
          />
          Show deactivated
        </label>
        {stats && (
          <span className="ml-auto font-mono text-xs text-muted-foreground">
            {stats.active} active · {stats.deactivated} deactivated
          </span>
        )}
      </div>

      {selected.size > 0 && (
        <Card className="mb-3 flex flex-wrap items-center gap-3 p-3" data-testid="users-bulk-bar">
          <span className="text-sm font-medium" data-testid="users-selected-count">
            {selected.size} selected
          </span>
          <Button
            size="sm"
            variant="outline"
            disabled={bulkBusy}
            onClick={() => runBulk("deactivate")}
            data-testid="users-bulk-deactivate"
            className="gap-1.5"
          >
            {bulkBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Ban className="h-3.5 w-3.5" />}
            Deactivate selected
          </Button>
          <Button
            size="sm"
            variant="destructive"
            disabled={bulkBusy}
            onClick={() => runBulk("delete")}
            data-testid="users-bulk-delete"
            className="gap-1.5"
          >
            {bulkBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
            Delete selected
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())} disabled={bulkBusy}>
            Clear
          </Button>
        </Card>
      )}

      {bulkReport && (
        <Card className="mb-3 p-3" data-testid="users-bulk-report">
          <div className="mb-2 flex items-center gap-2 text-sm">
            <span className="font-medium">
              Bulk {bulkReport.kind === "delete" ? "delete" : "deactivate"}:
            </span>
            <span>{bulkReport.ok} done</span>
            {bulkReport.pending > 0 && (
              <span className="text-destructive">· {bulkReport.pending} not confirmed</span>
            )}
            {bulkReport.error > 0 && <span className="text-destructive">· {bulkReport.error} rejected</span>}
            <Button
              size="sm"
              variant="ghost"
              className="ml-auto h-6 px-2 text-[10px]"
              onClick={() => setBulkReport(null)}
            >
              Dismiss
            </Button>
          </div>
          {(bulkReport.results || []).some((r) => r.status !== "ok") && (
            <ul className="space-y-0.5 text-xs text-muted-foreground">
              {(bulkReport.results || [])
                .filter((r) => r.status !== "ok")
                .map((r) => (
                  <li key={r.user_id} className="font-mono">
                    <span className={r.status === "pending" ? "text-amber-600" : "text-destructive"}>
                      {r.status}
                    </span>{" "}
                    {r.email || r.user_id} — {r.detail}
                  </li>
                ))}
            </ul>
          )}
        </Card>
      )}

      {!items && <Skeleton className="h-64" />}
      {items && items.length === 0 && (
        <Card className="p-12 text-center text-sm text-muted-foreground" data-testid="users-empty">
          <UsersIcon className="mx-auto mb-3 h-8 w-8" strokeWidth={1.5} />
          No users match your search.
        </Card>
      )}
      {items && items.length > 0 && (
        <Card className="overflow-hidden p-0">
          <div className="overflow-x-auto">
          <table className="min-w-full text-sm" data-testid="users-table">
            <thead className="thead">
              <tr>
                <th className="w-10 px-3 py-2">
                  <Checkbox
                    data-testid="users-select-all"
                    checked={allSelectedOnPage}
                    onCheckedChange={toggleSelectAll}
                    aria-label="Select all"
                  />
                  {someSelectedOnPage && (
                    <span className="ml-1 inline-block h-1 w-1 rounded-full bg-primary align-middle" />
                  )}
                </th>
                <th className="px-4 py-2 text-left">User</th>
                <th className="px-4 py-2 text-left">Roles</th>
                <th className="px-4 py-2 text-right"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((u) => {
                const isSelf = u.id === me.id;
                const deleted = !!u.deleted;
                const dealerNames = u.dealer_names || (u.dealer_name ? [u.dealer_name] : []);
                return (
                  <Fragment key={u.id}>
                    <tr
                      className={`border-t border-border ${deleted ? "opacity-50" : ""}`}
                      data-testid={`user-row-${u.id}`}
                    >
                      <td className="px-3 pt-2 pb-1 align-top">
                        {/* You can never bulk-action yourself, so no checkbox. */}
                        {!isSelf && (
                          <Checkbox
                            data-testid={`user-select-${u.id}`}
                            checked={selected.has(u.id)}
                            onCheckedChange={() => toggleOne(u.id)}
                            aria-label={`Select ${u.email}`}
                          />
                        )}
                      </td>
                      <td className="px-4 pt-2 pb-1">
                        <div className="font-mono text-xs">
                          {u.email}
                          {isSelf && (
                            <Badge variant="outline" className="ml-2 font-mono text-[9px]">
                              you
                            </Badge>
                          )}
                        </div>
                        <div className="text-sm">{u.name}</div>
                      </td>
                      <td className="px-4 pt-2 pb-1">
                        <div className="flex flex-wrap gap-1">
                          {u.roles.map((r) => (
                            <span
                              key={r}
                              className="rounded-full border border-border bg-secondary px-2 py-0.5 font-mono text-[9px] uppercase tracking-wider text-muted-foreground"
                            >
                              {roleLabel(r, lang)}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="px-4 pt-2 pb-1 text-right">
                        <div className="flex justify-end gap-1">
                          {!deleted && (
                            <>
                              <Button
                                size="sm"
                                variant="ghost"
                                data-testid={`edit-user-btn-${u.id}`}
                                onClick={() => setEditing(u)}
                                title="Edit"
                              >
                                <Pencil className="h-3.5 w-3.5" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                data-testid={`reset-pw-btn-${u.id}`}
                                onClick={() => setResetting(u)}
                                title="Reset password"
                              >
                                <KeyRound className="h-3.5 w-3.5" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                data-testid={`deactivate-btn-${u.id}`}
                                onClick={() => setDeactivating(u)}
                                disabled={isSelf}
                                title={isSelf ? "You cannot deactivate yourself" : "Deactivate"}
                              >
                                <Ban className="h-3.5 w-3.5 text-destructive" />
                              </Button>
                            </>
                          )}
                          {deleted && (
                            <>
                              <Button
                                size="sm"
                                variant="ghost"
                                data-testid={`restore-btn-${u.id}`}
                                onClick={() => restore(u)}
                                disabled={busyId === u.id}
                                title="Restore"
                              >
                                <RotateCcw className="h-3.5 w-3.5" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                data-testid={`hard-delete-btn-${u.id}`}
                                onClick={() => setDeleting(u)}
                                disabled={isSelf}
                                title={isSelf ? "You cannot delete yourself" : "Delete permanently"}
                              >
                                <Trash2 className="h-3.5 w-3.5 text-destructive" />
                              </Button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                    <tr className={deleted ? "opacity-50" : ""} data-testid={`user-row-secondary-${u.id}`}>
                      <td colSpan={4} className="px-4 pb-2 pt-0">
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-muted-foreground">
                          <span className="inline-flex items-center gap-1" data-testid={`user-dealers-cell-${u.id}`}>
                            {t("users.columns.dealers")}:{" "}
                            {dealerNames.length > 0 ? (
                              <span className="inline-flex items-center gap-1">
                                {dealerNames.length}
                                <DealersInfoPopover names={dealerNames} t={t} />
                              </span>
                            ) : (
                              "—"
                            )}
                          </span>
                          <span className="font-mono text-[10px]">
                            Last login: {u.last_login_at ? u.last_login_at.slice(0, 16).replace("T", " ") : "never"}
                          </span>
                          <SyncStatusBadge user={u} onRetry={retrySync} busy={busyId === u.id} />
                        </div>
                      </td>
                    </tr>
                  </Fragment>
                );
              })}
            </tbody>
          </table>
          </div>
        </Card>
      )}

      <CreateUserDialog
        open={creating}
        onOpenChange={setCreating}
        onCreated={() => {
          refresh();
          refreshAssignableDealers();
        }}
        me={me}
        assignableDealers={assignableDealers}
        lang={lang}
        t={t}
      />
      <EditUserDialog
        open={!!editing}
        onOpenChange={(o) => !o && setEditing(null)}
        user={editing}
        onSaved={() => {
          refresh();
          refreshAssignableDealers();
        }}
        me={me}
        assignableDealers={assignableDealers}
        lang={lang}
        t={t}
      />
      <ResetPasswordDialog open={!!resetting} onOpenChange={(o) => !o && setResetting(null)} user={resetting} />

      <AlertDialog open={!!deactivating} onOpenChange={(o) => !o && setDeactivating(null)}>
        <AlertDialogContent data-testid="deactivate-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Deactivate {deactivating?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              <span className="font-mono">{deactivating?.email}</span> will no longer be able to sign in. You can restore this account later from the &quot;Show deactivated&quot; view.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={!!busyId}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={deactivate}
              disabled={!!busyId}
              data-testid="confirm-deactivate-btn"
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Deactivate
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent data-testid="hard-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Permanently delete {deleting?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              <span className="font-mono">{deleting?.email}</span> will be removed from Keycloak and the CMS, then
              deleted here. This cannot be undone. If either system is unreachable, nothing is deleted anywhere —
              use the sync badge&apos;s Retry once it&apos;s back.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={!!busyId}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={hardDelete}
              disabled={!!busyId}
              data-testid="confirm-hard-delete-btn"
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete permanently
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
